import {
  SetAuthData,
  SetRegistrationData,
  SetAuthError,
  ClearErrors,
  SET_AUTH_DATA,
  SET_AUTH_ERROR,
  CLEAR_ERRORS,
  SET_REGISTRATION_DATA
} from '../actions';
import { StoreState, SetupState, getAuthAttribute } from '../types';
import { createSelector } from 'reselect';
import { parseJWTToken } from '../utils';

// reducers ------------------------------------------------------------------

export default {
  authData,
  registrationData,
  authError
};

export function authData(state: StoreState['authData'], action: SetAuthData) {
  switch (action.type) {
    case SET_AUTH_DATA:
      return action.authData || null;
    default:
      return state || null;
  }
}

export function registrationData(
  state: StoreState['registrationData'],
  action: SetRegistrationData
) {
  switch (action.type) {
    case SET_REGISTRATION_DATA:
      return action.registrationData || null;
    default:
      return state || null;
  }
}

export function authError(
  state: StoreState['authError'],
  action: SetAuthError | ClearErrors
) {
  switch (action.type) {
    case SET_AUTH_ERROR:
      return action.error || null;
    case CLEAR_ERRORS:
      return null;
    default:
      return state || null;
  }
}

// selectors -----------------------------------------------------------------

export function getAuthData(state: StoreState) {
  return state.authData;
}

export function getRegistrationData(state: StoreState) {
  return state.registrationData;
}

export function getAuthError(state: StoreState) {
  return state.authError;
}

export const getAccessToken = createSelector(
  getAuthData,
  data => {
    return data ? data.accessToken : undefined;
  }
);

export const getIdToken = createSelector(
  getAuthData,
  data => {
    return data ? data.idToken : undefined;
  }
);

export const getJwtPayload = createSelector(
  getIdToken,
  idToken => {
    if (!idToken) {
      return null;
    }
    return parseJWTToken(idToken);
  }
);

export const getMfaRequired = createSelector(
  getJwtPayload,
  data => {
    return data ? data.mfa_required === 'true' : undefined;
  }
);

export const getMfaComplete = createSelector(
  getJwtPayload,
  getMfaRequired,
  (data, mfaRequired) => {
    if (!data) {
      return undefined;
    }

    // if MFA is not required for this user, we consider their
    // MFA config to be complete
    if (!mfaRequired) {
      return true;
    }

    return data.mfa_complete === 'true';
  }
);

export const getUsername = createSelector(
  getAuthData,
  data => {
    return data ? data.email : undefined;
  }
);

export const getRegistrationToken = createSelector(
  getRegistrationData,
  data => {
    return data ? data.registrationToken : undefined;
  }
);

export const getIsImaUser = createSelector(
  getAuthData,
  data => {
    if (!data) {
      return false;
    }
    return data.isImaUser;
  }
);

export const getFirstName = createSelector(
  getAuthData,
  data => {
    return data ? getAuthAttribute(data, 'given_name') : undefined;
  }
);

export const getLastName = createSelector(
  getAuthData,
  data => {
    return data ? getAuthAttribute(data, 'family_name') : undefined;
  }
);

export const getPhoneNumber = createSelector(
  getAuthData,
  data => {
    return data ? getAuthAttribute(data, 'phone_number') : undefined;
  }
);

export const getPhoneNumberVerified = createSelector(
  getAuthData,
  data => {
    return data
      ? getAuthAttribute<'true' | 'false'>(data, 'phone_number_verified')
      : undefined;
  }
);

export const getNewPasswordRequired = createSelector(
  getAuthData,
  data => {
    return data ? data.newPasswordRequired : undefined;
  }
);

export const getAgreedToTos = createSelector(
  getAuthData,
  data => {
    return data ? data.agreedToTos : undefined;
  }
);

export const getFederated = createSelector(
  getAuthData,
  data => {
    return data ? data.federated : undefined;
  }
);

export const getUserSetupComplete = createSelector(
  getNewPasswordRequired,
  getMfaComplete,
  (newPasswordRequired, mfaComplete) => {
    return !newPasswordRequired && mfaComplete;
  }
);

export const getUserSetupState = createSelector(
  getNewPasswordRequired,
  getMfaComplete,
  (newPasswordRequired, mfaComplete): SetupState => {
    if (newPasswordRequired) {
      return SetupState.needsPassword;
    }

    if (!mfaComplete) {
      return SetupState.needsMfa;
    }

    return SetupState.complete;
  }
);
